import { FormEvent, useRef, useState } from "react";
import { submitAudience, type AudiencePayload } from "../api";
import { useLanguage } from "../i18n";
import { ArrowIcon } from "./ArrowIcon";
import { TurnstileWidget, type TurnstileHandle } from "./TurnstileWidget";

type AudienceFormProps = {
  kind: "family" | "contact";
};

export function AudienceForm({ kind }: AudienceFormProps) {
  const { language, t } = useLanguage();
  const [status, setStatus] = useState<"idle" | "sending" | "success" | "error">("idle");
  const [message, setMessage] = useState("");
  const [turnstileToken, setTurnstileToken] = useState("");
  const turnstileRef = useRef<TurnstileHandle>(null);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!turnstileToken) {
      setStatus("error");
      setMessage(t.form.turnstileRequired);
      return;
    }
    const form = event.currentTarget;
    const data = new FormData(form);
    const payload: AudiencePayload = {
      kind,
      name: String(data.get("name") || ""),
      email: String(data.get("email") || ""),
      city: String(data.get("city") || ""),
      message: kind === "contact" ? String(data.get("message") || "") : undefined,
      musicPreference: kind === "family" ? String(data.get("musicPreference") || "") : undefined,
      marketingOptIn: data.get("marketingOptIn") === "on",
      language,
      sourcePage: window.location.pathname + window.location.hash,
      privacyVersion: "2026-08-15",
      turnstileToken,
    };

    setStatus("sending");
    setMessage("");
    try {
      await submitAudience(payload);
      setStatus("success");
      setMessage(kind === "contact" ? t.form.contactSuccess : t.form.familySuccess);
      form.reset();
    } catch (error) {
      setStatus("error");
      setMessage(error instanceof Error ? error.message : t.form.genericError);
    } finally {
      turnstileRef.current?.reset();
    }
  }

  return (
    <form className="axon-form" onSubmit={handleSubmit}>
      <div className="form-row">
        <label>
          <span>{t.form.name}</span>
          <input name="name" autoComplete="name" required placeholder={t.form.namePlaceholder} />
        </label>
        <label>
          <span>{t.form.email}</span>
          <input name="email" type="email" autoComplete="email" required placeholder="you@email.com" />
        </label>
      </div>
      {kind === "family" ? (
        <div className="form-row">
          <label>
            <span>{t.form.city}</span>
            <input name="city" autoComplete="address-level2" placeholder={t.form.cityPlaceholder} />
          </label>
          <label>
            <span>{t.form.sound}</span>
            <select name="musicPreference" defaultValue="">
              <option value="" disabled>{t.form.choose}</option>
              <option value="House">{t.form.house}</option>
              <option value="Afro House">{t.form.afro}</option>
              <option value="Melodic Techno">{t.form.melodicTechno}</option>
              <option value="All">{t.form.all}</option>
            </select>
          </label>
        </div>
      ) : (
        <label>
          <span>{t.form.message}</span>
          <textarea name="message" required rows={4} placeholder={t.form.messagePlaceholder} />
        </label>
      )}
      <label className="check-label">
        <input name="marketingOptIn" type="checkbox" required={kind === "family"} />
        <span>{t.form.optIn}</span>
      </label>
      <p className="form-privacy-note">
        {language === "de" ? <>Mit dem Absenden bestätigst du, dass du unsere <a href="/datenschutz" target="_blank" rel="noreferrer">Datenschutzerklärung</a> zur Kenntnis genommen hast. Wir verarbeiten deine Kontaktdaten zur Bearbeitung deiner Anfrage bzw. Anmeldung.</> : <>By submitting, you confirm that you have read our <a href="/datenschutz" target="_blank" rel="noreferrer">privacy policy</a>. We process your contact details to handle your request or sign-up.</>}
      </p>
      <TurnstileWidget
        ref={turnstileRef}
        action={kind === "contact" ? "contact" : "family_join"}
        onToken={setTurnstileToken}
        onError={() => {
          setStatus("error");
          setMessage(t.form.turnstileError);
        }}
      />
      <div className="form-actions">
        <button className="button button--light" disabled={status === "sending" || !turnstileToken} type="submit">
          {status === "sending" ? t.form.sending : kind === "contact" ? t.form.send : t.form.join}
          <ArrowIcon />
        </button>
      </div>
      {message && <p className={`form-status form-status--${status}`} role="status">{message}</p>}
    </form>
  );
}
